// ---- QA-derived viability model (the menu is gated by what QA blessed) ------
// A menu combo is channel × platform × tactic × KPI (the QA grain); a path is
// viable when any rung of its ladder passes. QA emits eligibility per outcome,
// so `viable` gates the analysis and the per-outcome flags gate which outcomes
// that analysis may run.
export interface PathViability {
  viable: boolean;
  volume: boolean;
  revenue: boolean;
}

// Pools platform (channel_tactic) or tactic (channel_platform). Only `channel`
// pools both, and only scaling cards offer it; forecast never does.
// Order here is load-bearing: GRAIN_ORDER derives its sort from it.
export const Grain = {
  FINEST: 'finest',
  CHANNEL_PLATFORM: 'channel_platform',
  CHANNEL_TACTIC: 'channel_tactic',
  CHANNEL: 'channel'
} as const;
export type GrainType = (typeof Grain)[keyof typeof Grain];

// Which QA ladder a set of menus is built from. A card is exactly one of these
// (Card.forecast / Card.scaling, kept as booleans so forecast code paths are unchanged).
export const Analysis = {
  FORECAST: 'forecast',
  SCALING: 'scaling'
} as const;
export type AnalysisType = (typeof Analysis)[keyof typeof Analysis];

// Dropdown-only pseudo-values for the pooled ends of the Platform/Tactic
// dropdowns. Never sent, stored, or looked up -- resolved back to a real
// grain + a real platform/tactic before patchCard. Mirrors BudgetBuilder's
// NO_TACTIC_VALUE sentinel pattern.
export const ALL_PLATFORMS = '__all_platforms__';
export const ALL_TACTICS = '__all_tactics__';

// One selectable rung, with its own per-outcome eligibility.
export interface GrainOption {
  grain: GrainType;
  volume: boolean;
  revenue: boolean;
}

// Built per analysis: buildComboLookup / buildViability default to the forecast
// ladder, and with Analysis.SCALING the same fields describe the scaling ladder
// (`forecastGrains` then holds scaling grains). `scaling` is always the any-rung
// scaling aggregate either way.
export interface ComboInfo {
  /** Only `.viable` is read; per-outcome eligibility lives on `forecastGrains` instead. */
  forecast: { viable: boolean };
  /** Selectable rungs, finest → coarsest, after suppression. */
  forecastGrains: GrainOption[];
  /** QA's recommendation if it survived suppression, else the most granular survivor, else null. */
  recommendedGrain: GrainType | null;
  scaling: PathViability;
}

// The QA-derived viability lookups a card needs to build its menus and defaults.
export interface Viability {
  /** Aggregation-correct per-card ComboInfo for a card's actual grain
   *  options/outcome-gating (see forecastViabilityAt). */
  cardInfoFor: (ch: string, pl: string, tac: string, kpi: string) => ComboInfo | undefined;
  tacticsFor: (ch: string, pl: string) => string[];
  /** Union of tactics across every platform under the channel (for "All platforms"). */
  tacticsForChannel: (ch: string) => string[];
  platformViableForKpi: (ch: string, pl: string, kpi: string) => boolean;
  /** Is "All tactics" selectable for this platform (the channel_platform rung)? */
  allTacticsViableForKpi: (ch: string, pl: string, kpi: string) => boolean;
  /** Is tactic `tac` selectable pooled across every platform (the channel_tactic rung)? */
  tacticViableForKpi: (ch: string, tac: string, kpi: string) => boolean;
  /** Is "All platforms" itself selectable -- any channel-wide tactic (or, for
   *  scaling, the channel-only rung) viable under it? */
  allPlatformsViableForKpi: (ch: string, kpi: string) => boolean;
  /** Is channel-only ("All platforms" + "All tactics") viable? Always false on
   *  the forecast Viability. */
  channelViableForKpi: (ch: string, kpi: string) => boolean;
  /** First channel-wide tactic viable under "All platforms". */
  firstChannelTactic: (ch: string, kpi: string) => string | undefined;
  /** Is the (platform, tactic) combo still viable at `grain` for `kpi`? */
  comboViableAtGrain: (
    ch: string,
    pl: string,
    tac: string,
    kpi: string,
    grain: GrainType
  ) => boolean;
  platformsByChannel: Record<string, string[]>;
  /** Best real tactic + grain for a specific platform (finest, else pooled
   *  tactic if the platform supports it, else first-available). */
  bestTacticFor: (ch: string, pl: string, kpi: string) => { tactic: string; grain: GrainType };
  /** Picks platform + tactic + grain together from scratch, per the priority
   *  order (platform+tactic, platform+All tactics, All platforms+tactic, then,
   *  scaling only, All platforms+All tactics). */
  defaultSelection: (
    ch: string,
    kpi: string
  ) => { platform: string; tactic: string; grain: GrainType };
}

export interface Comparison {
  id: number;
  pct: string;
  // The 100% (primary) scenario: always sent to the forecasting pipeline regardless
  // of what's picked here, so it's shown disabled rather than as a pickable row.
  locked?: boolean;
}

export interface Card {
  id: number;
  channel: string;
  platform: string;
  tactic: string;
  kpi: string;
  /** Rung this card submits at; `platform`/`tactic` stay populated (blanked only at submit/display). */
  grain: GrainType;
  volume: boolean;
  revenue: boolean;
  forecast: boolean;
  scaling: boolean;
  primary: string;
  comparisons: Comparison[];
  /** Scaling cards only: the max weekly spend to test, as typed (sent as a number). */
  maxWeeklySpend: string;
  /** Scaling cards only, FE-only: what the "Total budget" field is a budget for. */
  budgetPeriod: BudgetPeriodType;
}

// ---- Scaling card: max weekly budget to test ---------------------------------
// Everything is checked and sent weekly; the period only converts a total budget
// to its weekly equivalent for display.
export const BudgetPeriod = {
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year'
} as const;
export type BudgetPeriodType = (typeof BudgetPeriod)[keyof typeof BudgetPeriod];

export const WEEKS_PER_PERIOD: Record<BudgetPeriodType, number> = {
  week: 1,
  month: 52 / 12,
  quarter: 13,
  year: 52
};

export const BUDGET_PERIOD_LABELS: Record<BudgetPeriodType, string> = {
  week: 'Week',
  month: 'Month',
  quarter: 'Quarter',
  year: 'Year'
};

// Multiples of the highest observed week: the prefill, the quick picks, and the
// point past which the curve is mostly extrapolation (soft warning).
export const SCALING_PREFILL_MULTIPLE = 2;
export const SCALING_QUICK_PICK_MULTIPLES = [1.5, 2, 3];
export const SCALING_WARN_MULTIPLE = 3;

// Future-only date range, capped at ~3 months (Figma rule).
const MS_PER_DAY = 864e5;
const isoInDays = (n: number) => new Date(Date.now() + n * MS_PER_DAY).toISOString().slice(0, 10);
export const TODAY_ISO = isoInDays(0);
export const MAX_ISO = isoInDays(92); // hard cap, ~3 months out
export const DEFAULT_START = isoInDays(7); // default window opens 1 week out…
export const DEFAULT_END = isoInDays(84); // …and runs 12 weeks

// The locked row's pct, and the value buildStartNewForecastPayload always sends regardless
// of card state.
export const PRIMARY_COMPARISON_PCT = 100;
// What a freshly added (unlocked) comparison row starts at.
export const NEW_COMPARISON_PCT = '110';

// Floor applies to the removable (non-locked) rows only.
export const MINIMUM_COMPARISON_BUDGETS = 6;
export const DEFAULT_COMPARISON_BUDGETS: Comparison[] = [
  { id: 1, pct: '50' },
  { id: 2, pct: '75' },
  { id: 3, pct: String(PRIMARY_COMPARISON_PCT), locked: true },
  { id: 4, pct: '125' },
  { id: 5, pct: '150' },
  { id: 6, pct: '175' },
  { id: 7, pct: '200' }
];

// Highest id the default budgets occupy. New comparison/card ids are minted above
// this so they can never collide with a seeded default, no matter how the list grows.
export const MAX_DEFAULT_COMPARISON_ID = Math.max(...DEFAULT_COMPARISON_BUDGETS.map(c => c.id));

// Where a scaling card's highest-week anchor stands. UNAVAILABLE = this QA run
// can't anchor the card (no series file, or no full week for its rows);
// FAILED = the series fetch errored even after retries (the card offers a retry).
export const AnchorStatus = {
  LOADING: 'loading',
  READY: 'ready',
  UNAVAILABLE: 'unavailable',
  FAILED: 'failed'
} as const;
export type AnchorStatusType = (typeof AnchorStatus)[keyof typeof AnchorStatus];
